import { Inject } from '@angular/core';
import { interval } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, OnInit, AfterViewInit, HostListener, ViewEncapsulation, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Validators } from '@angular/forms';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { LogEventStrings } from '@app/utilities/log-event-strings';
import { LoggingService } from '@app/services/logging.service';
import { LogEventCodes } from '@app/utilities/log-event-codes';
import { AuthorizationService } from '@app/services/authorization.service';
import { ArisMessageService } from '@app/services/aris-message.service';
import { AdminEmailService } from '@app/services/admin-email.service';
import { LocalStorageUtilities } from '@app/utilities/local-storage-utilities';
import { PersonPortalState } from '@app/models/person-portal-state';
import { Mxna } from '@app/models/mxna';
import { SpotPrice } from '@app/models/spot-price';
import { RefreshToken } from '@app/models/refresh-token';
import { AbstractControl, FormGroup, FormControl } from '@angular/forms';
import { StringUtilities } from '@app/utilities/string-utilities';
import { UppercaseFirstLetterPipe } from '@app/pipes/uppercase-first-letter.pipe';
import { Webauthentication } from '@app/models/webauthentication';
import { EmailTemplateCodes } from '@app/utilities/email-template-codes';
import { HttpStatusCodes } from '@app/utilities/http-status-codes';
import { ArisMessageCodes } from '@app/utilities/aris-message-codes';
import { BnNgIdleService } from 'bn-ng-idle';
import { VoiceConnectionDetails } from '@app/models/voice-connection-details';
import { SwUpdate } from '@angular/service-worker';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { CustomerDocument } from '@app/models/customer-document';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.css'],
  providers: [UppercaseFirstLetterPipe],
  animations: [
    trigger('showMenu', [
      state('false', style({ width: '0px' })),
      state('true', style({ width: '255px' })),
      transition('false => true', animate('200ms ease-in')),
      transition('true => false', animate('200ms ease-in')),
    ]),
    trigger('routeAnimations', [
      transition('* => Summary', [
        style({ transform: 'translateX(-100%)' }),
        animate('500ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition('* => AccountStatus', [
        style({ transform: 'translateX(-100%)' }),
        animate('500ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition('* => AccountHistory', [
        style({ transform: 'translateX(-100%)' }),
        animate('500ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition('* => EDocuments', [
        style({ transform: 'translateX(-100%)' }),
        animate('500ms 200ms', style({ transform: 'translateX(0%)' }))
      ]),
      transition('* => ESign', [
        style({ transform: 'translateX(-100%)' }),
        animate('500ms 200ms', style({ transform: 'translateX(0%)' }))
      ]),
      transition('* => DepositoryConfirmation', [
        style({ transform: 'translateX(-100%)' }),
        animate('500ms 200ms', style({ transform: 'translateX(0%)' }))
      ]),
      transition('* => DepositoryConfirmation', [
        style({ transform: 'translateX(-100%)' }),
        animate('500ms 200ms', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('400ms ease-in', style({ transform: 'translateX(-100%)' }))
      ])
    ])
  ],
  encapsulation: ViewEncapsulation.None
})
export class MainComponent implements OnInit, AfterViewInit {

  static readonly PANEL_WIDTH_OPEN: number = 0;
  static readonly PANEL_WIDTH_CLOSED: number = -300;
  static readonly NOTIFICATION_PANEL_OPEN = 0;
  static readonly NOTIFICATION_PANEL_CLOSED = -220;
  static readonly TIMEOUT_DURATION_PROD: number = 1800;
  static readonly TIMEOUT_DURATION_TEST: number = 15;
  static readonly VERSION_TEXT: string = 'Version: 1.5.3';
  static readonly SUBTITLE_TEXT: string = 'Monex Precious Metals';
  // Properties.
  public showOverlay: boolean = false;
  public version: string = MainComponent.VERSION_TEXT;
  public subtitle: string = MainComponent.SUBTITLE_TEXT;
  public mainAccount: string;
  public personName: string;
  public successPanelHeaderText: string;
  public isMenuShowing: Boolean = false;
  public notificatonPanelPosition: number = MainComponent.NOTIFICATION_PANEL_CLOSED;
  public isNewVersionAvailable: Boolean = false;
  public changePasswordPanelPosition: number = MainComponent.PANEL_WIDTH_CLOSED;
  public passwordChangeSuccessPanelPosition: number = MainComponent.PANEL_WIDTH_CLOSED;
  public changeUserIdPanelPosition: number = MainComponent.PANEL_WIDTH_CLOSED;
  public settingsPanelPosition: number = MainComponent.PANEL_WIDTH_CLOSED;
  public contactUsPanelPosition: number = MainComponent.PANEL_WIDTH_CLOSED;
  public failedActionPanelPosition: number = MainComponent.PANEL_WIDTH_CLOSED;
  public userIdChangeSuccessPanelPosition: number = MainComponent.PANEL_WIDTH_CLOSED;
  public personPortalState: PersonPortalState;
  public activeAccount: Mxna;
  // Combobox model.
  public comboBoxItems: Mxna[];
  // Active child component
  public componentReference: any;
  // Password field visiblity indicators.
  public newPasswordFieldHide = true;
  public confirmPasswordFieldHide = true;
  // Utilities visibility
  // Prevents button from receiving a click event even though it might be outside the popover's viewport.
  public isUtilitiesPanelShowing: boolean = true;
  // For customer phone number validator.
  public isValidFlg: boolean = true;
  //
  private newVersionAvailable: NgbPopover
  private checkForUpdateInterval: any = interval(30000);
  private checkForUpdateIntervalSubscribe: any;
  // Side menu
  // Use static: false because the menu might be subject to change/animations
  @ViewChild('sideMenuContainer', { static: false }) sideMenuContainer: ElementRef;

  // (Reactive aka Model Driven) Form groups.
  public changePasswordFormGroup: FormGroup = new FormGroup({
    newPasswordFormControl: new FormControl('', [Validators.required, Validators.maxLength(35), Validators.minLength(6),]),
    confirmPasswordFormControl: new FormControl('', [Validators.required, Validators.maxLength(35), Validators.minLength(6),]),
  }, this.pwdMatchValidator);

  public changeUserIdFormGroup: FormGroup = new FormGroup({
    newUserIdFormControl: new FormControl('', [Validators.required, Validators.maxLength(35), Validators.minLength(6), this.testValidator,]),
    confirmUserIdFormControl: new FormControl('', [Validators.required, Validators.maxLength(35), Validators.minLength(6), this.testValidator]),
  }, this.userIdMatchValidator);

  public contactUsFormGroup: FormGroup = new FormGroup({
    nameFormControl: new FormControl('', [Validators.required, Validators.maxLength(35), Validators.minLength(6),]),
    phoneNumberFormControl: new FormControl('', [Validators.required, Validators.minLength(14)]),
  });

  public settingsFormGroup: FormGroup = new FormGroup({
    paperlessFormControl: new FormControl('false', Validators.requiredTrue),
  });

  constructor(private elementRef: ElementRef,
    private router: Router,
    private route: ActivatedRoute,
    private loggingService: LoggingService,
    private authorizationService: AuthorizationService,
    private bnIdle: BnNgIdleService,
    private arisMessageService: ArisMessageService,
    private adminEmailService: AdminEmailService,
    private swUpdate: SwUpdate,
    @Inject(DOCUMENT) private document: Document) { }

  /**
   * Listens for clicks in children of the main screen.
   * @param event 
   */
  @HostListener('document:click', ['$event'])
  public documentClick(event: Event): void {
    // Reset the timeout timer every time the user interacts with component.
    this.resetSessionTimeOutTimer();

    // 2. Close menu logic
    if (this.isMenuShowing && this.sideMenuContainer) {
      const clickedElement = event.target as HTMLElement;
      // Check if the click was inside the side menu
      const isInsideMenu = this.sideMenuContainer.nativeElement.contains(clickedElement);
      // Check if the click was on the hamburger/menu button
      // We check for the 'hamburger-container' class which you use in your HTML
      const isHamburgerClick = clickedElement.closest('.hamburger-container');
      // Only close if it's NOT in the menu AND NOT the button that opens it
      if (!isInsideMenu && !isHamburgerClick) {
        this.closeMenu();
      }
    }
  }

  /**
   * Setter needed because the popover element is hidden
   * with *ngIf and, thus, is undefined.  When the element is made visible, the
   * setter is invoked and the reference now points to the element.
   */
  @ViewChild('popOver', { static: false }) set setPopover(popover: NgbPopover) {
    if (popover != undefined) {
      this.newVersionAvailable = popover;
      this.showNewVersionPopover();
    }
  }
  @ViewChild('utilitiesPopOver') public utilitiesPopOver: NgbPopover;

  /**
   * This lifecycle method is invoked when the page is refreshed.
   */
  public ngOnInit(): void {
    this.initControls();
    // Load from the database, all of the person's account numbers. These
    // will be loaded in the account number combobox's backing model.
    this.findAllAccounts();
    this.initSessionTimer();
  }

  public ngAfterViewInit(): void {
    this.promptForVersionUpdate();
  }

  public ngOnDestroy(): void {
    this.bnIdle.stopTimer();
    this.checkForUpdateIntervalSubscribe?.unsubscribe();
  }

  private initControls() {
    // Load person state.
    this.personPortalState = LocalStorageUtilities.getPersonPortalState();
    this.personName = (this.personPortalState.micaAccountName != null) ? StringUtilities.uppercaseFirstLetter(this.personPortalState.micaAccountName) : '';
    this.activeAccount = this.personPortalState.mxna;
    this.notificatonPanelPosition = (this.personPortalState.webauthenticationId.passwordExpirationTime != null) ? MainComponent.NOTIFICATION_PANEL_OPEN : MainComponent.NOTIFICATION_PANEL_CLOSED;
  }

  private initSessionTimer(): void {
    // Create a timeout timer.  This needs to be (re)created if the page is
    // refreshed. Test with 15, Prod with 1800
    this.bnIdle.startWatching(MainComponent.TIMEOUT_DURATION_PROD).subscribe((isTimedOut: boolean) => {
      if (isTimedOut) {
        // Check if an "onTimout" method exists in the component, and,
        // if so, invoke the method.
        if ("onTimeout" in this.componentReference) {
          this.componentReference.onTimeout();
        }
        this.toggleOverlay();
      }
    });
  }

  private findAllAccounts(): void {
    let mainAccount = this.personPortalState.acctNum.slice(0, -1);
    this.authorizationService.findAccountsByMainAccount(mainAccount).subscribe({
      next: (accts: Mxna[]) => {
        this.comboBoxItems = accts;
      },
    });
  }

  // Called to initiate the opening and closing of the side drawer (menu).
  public toggleSideMenu() {
    //event.stopPropagation(); 
    this.isMenuShowing = !this.isMenuShowing;
  }

  public closeMenu() {
    this.isMenuShowing = false;
  }

  private stopSessionTimeOutTimer(): void {
    this.bnIdle.stopTimer();
  }

  private resetSessionTimeOutTimer(): void {
    this.bnIdle.stopTimer();
    this.bnIdle.resetTimer();
    this.refreshToken();
  }

  private toggleOverlay(): void {
    this.showOverlay = !this.showOverlay;
    if (this.showOverlay) {
      this.stopSessionTimeOutTimer();
    } else {
      this.resetSessionTimeOutTimer();
    }
  }

  public onOverlayTimeoutEvent(event: Event) {
    if (event["action"] == "done") {
      this.logout();
    }
  }

  /**
   * Prevents the click event from reaching the host component.  If 
   * it were otherwise, the overlay would close.
   * @param event 
   */
  public onOverlayClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  public onExtendSession(event: MouseEvent) {
    this.toggleOverlay();
    this.refreshToken();
  }

  private refreshToken(): void {
    // Ask for a refreshed token and then store it.
    this.authorizationService.refreshToken().subscribe({
      next: (refreshToken: RefreshToken) => {
        let personPortalState: PersonPortalState = LocalStorageUtilities.getPersonPortalState();
        personPortalState.token = refreshToken.token;
        LocalStorageUtilities.setPersonPortalState(personPortalState);
      },
      error: (err: any) => {
        console.log(`token error`);
      }
    })
  }

  /**
   * 2020-10-03 - Added logic to handle the use case where the activated
   * account has no documents. Fixes the issue that when this case exists
   * the "Account Summary" information didn't update.
   * 
   * 2026-04-27 - Removed code that truncated commodity code before
   *              comparison is made (price.symbol === position.commodity.slice(0, 4).trim()). 
   * 
   * Invoked by selecting a new account in the template's combobox.
   */
  public onActiveAccountChange(event: any): void {
    // Update local storage to reflect the state of the new active account.
    let mxna: Mxna = event.value;
    let personPortalState: PersonPortalState = LocalStorageUtilities.getPersonPortalState();
    personPortalState.acctNum = mxna.account;
    personPortalState.micaAccountName = mxna.accountName;
    personPortalState.mxna = mxna;
    // Update current market value                
    this.authorizationService.findDelayedPrices().subscribe({
      next: (prices: SpotPrice[]) => {
        personPortalState.mxna.mxPosTrade.forEach((position) => {
          prices.forEach((price: SpotPrice) => {
            if (price.symbol.trim() === position.commodity.trim()) {
              if (position.unitBal > 0) {
                position.currentMktVal = price.last_bid;
              } else {
                position.currentMktVal = price.last_ask;
              }
              position.extendedMktVal = position.unitBal * position.currentMktVal;
            }
          });
        })
        this.authorizationService.findDocuments(personPortalState.co, personPortalState.acctNum).subscribe({
          next: (customerDocuments: CustomerDocument[]) => {
            personPortalState.customerDocuments = customerDocuments;
            LocalStorageUtilities.clearPersonPortalState();
            LocalStorageUtilities.setPersonPortalState(personPortalState);
            // Refresh child component's UI.               
            this.componentReference.refreshUI();
          },
          error: (err: HttpErrorResponse) => {
            personPortalState.customerDocuments = [];
            LocalStorageUtilities.clearPersonPortalState();
            LocalStorageUtilities.setPersonPortalState(personPortalState);
            // Refresh child component's UI.               
            this.componentReference.refreshUI();
          }
        })
      },
      error: (err: HttpErrorResponse) => {
        console.log(`Error message ${err.message} and error code ${err.status}`);
        // If its the result of a 401 (timeout, etc.) error return the user to the landing screen.
        switch (err.status) {
          case HttpStatusCodes.UNAUTHORIZED:
            this.logout();
            break;
          default:
          // TODO: Decide what do with all other errors.
        }
      }
    });
  }

  /**
   * This is called from the outlet after the new module is loaded. The
   * method then invokes the "refreshUI" method on the new module;
   * @param componentReference 
   */
  public onActivate(componentReference: any) {
    // Inteface method.
    this.componentReference = componentReference;
    this.componentReference.refreshUI();
  }

  /**
   * Screen navigation methods.  Invoked from the side menu.
   */
  public openSummaryComponent(): void {
    // Note that navigation is URL based. Thus sibling to sibling navigation would be accomplished by "../Whatevercomponent"     
    this.router.navigate(['./summary'], { relativeTo: this.route });
    // this.toggleSideMenu();
  }

  public openAccountStatusComponent(): void {
    // Note that navigation is URL based. Thus sibling to sibling navigation would be accomplished by "../Whatevercomponent"     
    this.router.navigate(['./account-status'], { relativeTo: this.route });
    // this.toggleSideMenu();
  }

  public openAccountHistoryComponent(): void {
    // Note that navigation is URL based. Thus sibling to sibling navigation would be accomplished by "../Whatevercomponent"     
    this.router.navigate(['./account-history'], { relativeTo: this.route });
    // this.toggleSideMenu();
  }

  public openEdocumentsComponent(): void {
    // Note that navigation is URL based. Thus sibling to sibling navigation would be accomplished by "../Whatevercomponent"     
    this.router.navigate(['./edocuments'], { relativeTo: this.route });
    // this.toggleSideMenu();
  }

  public openEsignComponent(): void {
    // Note that navigation is URL based. Thus sibling to sibling navigation would be accomplished by "../Whatevercomponent"     
    this.router.navigate(['./esign'], { relativeTo: this.route });
  }

  public openDepositoryConfirmationComponent(): void {
    // Note that navigation is URL based. Thus sibling to sibling navigation would be accomplished by "../Whatevercomponent"     
    this.router.navigate(['./depository-confirmation'], { relativeTo: this.route });
    // this.toggleSideMenu();
  }

  /**
   * Commented out so new version could be deployed.
   */
  // public openProfileComponent(): void {     
  //   this.router.navigate(['./profile'], { relativeTo: this.route }); 
  //   this.hideUtilitiesPopover();   
  // }

  public submitChangePasswordForm(): void {
    this.saveNewPassword();
  }

  public logout(): void {
    // TODO: Consolidate this and what's in the authorization service.  Leave for now.
    LocalStorageUtilities.clearPersonPortalState();
    this.authorizationService.logout;
    this.router.navigate(['/landing']);
  }

  /**
   * Popover panel related methods.
   * 
   * "onPopoverShown" is utitilzed to programmatically set focus
   * in the main panel so that the Enter key can be used to close
   * the popover
   */
  public onPopoverShown(): void {
    // The element isn't shown immediately so post action to the event queue.
    setTimeout(() => this.setFocusOnElement("#main-items-panel"), 0);
  }

  public onShowFailedPanelAction(): void {
    this.onHideChangeUserIdPanelAction();
    this.failedActionPanelPosition = MainComponent.PANEL_WIDTH_OPEN;
    this.setFocusOnElement("#failed-panel-button");
  }

  public onHideFailedPanelAction(): void {
    this.failedActionPanelPosition = MainComponent.PANEL_WIDTH_CLOSED;
  }

  public onShowChangeEmailAddressPanelAction(): void {
    this.setFocusOnElement("#new-password-field");
    this.changePasswordPanelPosition = MainComponent.PANEL_WIDTH_OPEN;
    this.isUtilitiesPanelShowing = false;
  }

  public onHideChangeEmailAddressPanelAction(): void {
    // Clear all fields.
    this.changePasswordFormGroup.reset();
    this.isUtilitiesPanelShowing = true;
    this.changePasswordPanelPosition = MainComponent.PANEL_WIDTH_CLOSED;
  }

  public onShowSuccessEmailAddressPanelAction(): void {
    this.setFocusOnElement("#password-changed-success-panel");
    this.passwordChangeSuccessPanelPosition = MainComponent.PANEL_WIDTH_OPEN;
  }

  public onHideSuccessEmailAddressPanelAction(): void {
    this.isUtilitiesPanelShowing = true;
    this.setFocusOnElement("#main-items-panel");
    this.passwordChangeSuccessPanelPosition = MainComponent.PANEL_WIDTH_CLOSED;
  }

  public onShowChangeUserIdPanelAction(): void {
    this.setFocusOnElement("#new-user-id-field");
    this.changeUserIdPanelPosition = MainComponent.PANEL_WIDTH_OPEN;
    this.isUtilitiesPanelShowing = false;
  }

  public onHideChangeUserIdPanelAction(): void {
    // Clear all fields.
    this.changeUserIdFormGroup.reset();
    this.isUtilitiesPanelShowing = true;
    this.changeUserIdPanelPosition = MainComponent.PANEL_WIDTH_CLOSED;
  }

  public onShowContactUsPanelAction(): void {
    this.isUtilitiesPanelShowing = false;
    this.setFocusOnElement("#phone-name-id-field");
    this.contactUsPanelPosition = MainComponent.PANEL_WIDTH_OPEN;
  }

  public onHideContactUsPanelAction(): void {
    this.contactUsFormGroup.reset();
    this.isUtilitiesPanelShowing = true;
    this.contactUsPanelPosition = MainComponent.PANEL_WIDTH_CLOSED;
    //this.setFocusOnElement("#main-items-panel");
  }

  public onShowSettingsPanelAction(): void {
    this.isUtilitiesPanelShowing = false;
    //this.setFocusOnElement("#phone-name-id-field");
    this.settingsPanelPosition = MainComponent.PANEL_WIDTH_OPEN;
  }

  public onHideSettingsPanelAction(): void {
    //this.contactUsFormGroup.reset();
    this.isUtilitiesPanelShowing = true;
    this.settingsPanelPosition = MainComponent.PANEL_WIDTH_CLOSED;
    //this.setFocusOnElement("#main-items-panel");
  }

  public showNotificationPanel(): void {
    this.notificatonPanelPosition = MainComponent.NOTIFICATION_PANEL_OPEN;
  }

  public hideNotificationPanel(): void {
    this.notificatonPanelPosition = MainComponent.NOTIFICATION_PANEL_CLOSED;
  }

  public showNewVersionPopover(): void {
    this.newVersionAvailable.open();
  }

  public hideUtilitiesPopover(): void {
    console.log(this)
    this.utilitiesPopOver.isOpen() ? this.utilitiesPopOver.close() : this.utilitiesPopOver.open();
  }

  /**
   * Save customer's password change.
   */
  public saveNewPassword() {
    let newPassword: string = this.changePasswordFormGroup.controls["newPasswordFormControl"].value;
    let webauthentication: Webauthentication = new Webauthentication();
    webauthentication.id = this.personPortalState.id;
    webauthentication.password = newPassword;
    webauthentication.passwordUpdateDate = new Date();
    // Update MICA via REST service.   
    this.authorizationService.savePassword(webauthentication.id, newPassword).subscribe({
      next: (webauthentication: Webauthentication) => {
        // Close popover panel.        
        this.onHideChangeEmailAddressPanelAction();
        this.successPanelHeaderText = "Password Changed";
        this.onShowSuccessEmailAddressPanelAction();
        // Update state
        this.personPortalState = LocalStorageUtilities.getPersonPortalState();
        this.personPortalState.webauthenticationId.passwordExpirationTime = null;
        // Log changed password changed event.
        this.loggingService.logEvent(LogEventStrings.CUSTOMER_CHANGED_PASSWORD, LogEventCodes.CUSTOMER_CHANGED_PASSWORD_CODE);
        /**
          *  Emails
          * 
          */
        // Send customer an email so they know their password was successfully changed.
        this.authorizationService.findEmailTemplateByEmailId(EmailTemplateCodes.USER_PASSWORD_CHANGED)
          .subscribe({
            next: (template: any) => {
              let mxna: Mxna = this.personPortalState.mxna;
              let accountNum: string = mxna.account.slice(-4);
              // Inject values into email template variables.
              let html: string = StringUtilities.formatString(template.html, accountNum);
              // Email the new password message to the customer.                 
              this.authorizationService.sendCustomerEmail(mxna.mxk9.email, template.subject, html)
                .subscribe({
                  next: status => {
                    console.log(status.status);
                  },
                  error: (err: HttpErrorResponse) => {
                    console.log(err);
                  }
                })
            }
          })
        // Next, send the admin. email  
        let mxna: Mxna = this.personPortalState.mxna;
        this.adminEmailService.sendAdminEmail("Admin - User Changed Password", mxna.account);
      },
      error: (err: HttpErrorResponse) => {
        this.onShowFailedPanelAction();
        switch (err.status) {
          case HttpStatusCodes.FOUND: {
            console.log(`Error: ${err.message}`);
            break;
          }
          case HttpStatusCodes.INTERNAL_SERVER_ERROR: {
            console.log(`Error: ${err.message}`);
          }
        }
      }
    });
  }

  /**
   * Save the user's new User ID.
   */
  public saveNewUserId() {
    let newUserId: string = this.changeUserIdFormGroup.controls["newUserIdFormControl"].value;
    let webauthentication: Webauthentication = new Webauthentication();
    // This is the record's index.  This is needed for the SQL update.
    webauthentication.id = this.personPortalState.id;
    // Field's that will be updated with new values in the database.
    webauthentication.userName = newUserId;
    webauthentication.userNameUpdateDate = new Date();
    // Update MICA via REST service.
    //this.authorizationService.updateUserId(webauthentication, webauthentication.id).subscribe({
    this.authorizationService.saveUserId(webauthentication, webauthentication.id).subscribe({
      next: (webauthentication: Webauthentication) => {
        // Update state.
        this.personPortalState.userName = newUserId;
        this.personPortalState.webauthenticationId = webauthentication;
        this.personPortalState.webauthenticationId.userNameUpdateDate = new Date();
        LocalStorageUtilities.setPersonPortalState(this.personPortalState);
        // Close popover panel.        
        this.onHideChangeUserIdPanelAction();
        this.successPanelHeaderText = "User ID Changed";
        this.onShowSuccessEmailAddressPanelAction();
        /**
          *  Emails
          * 
          */
        // Send customer an email so he knows his password was changed successfully.
        this.authorizationService.findEmailTemplateByEmailId(EmailTemplateCodes.USER_ID_CHANGED)
          .subscribe({
            next: (template: any) => {
              let mxna: Mxna = this.personPortalState.mxna;
              let accountNum: string = mxna.account.slice(-4);
              // Inject values into email template variables.
              let html: string = StringUtilities.formatString(template.html, accountNum);
              // Email the new password message to the customer.                 
              this.authorizationService.sendCustomerEmail(mxna.mxk9.email, template.subject, html)
                .subscribe({
                  next: status => {
                    console.log(status.status);
                  },
                  error: (err: HttpErrorResponse) => {
                    console.log(err);
                  }
                })
            }
          })
        // Next, send the admin. email   
        let mxna: Mxna = this.personPortalState.mxna;
        this.adminEmailService.sendAdminEmail("Admin - User Changed User ID", mxna.account);
        // Log changed password changed event.
        this.loggingService.logEvent(LogEventStrings.CUSTOMER_CHANGED_USERID, LogEventCodes.CUSTOMER_CHANGED_USERID_CODE);
      },
      error: (err: HttpErrorResponse) => {
        this.onShowFailedPanelAction();
        switch (err.status) {
          case HttpStatusCodes.FOUND: {
            console.log(`Error: ${err.message}`);
            break;
          }
          case HttpStatusCodes.INTERNAL_SERVER_ERROR: {
            console.log(`Error: ${err.message}`);
          }
        }
      }
    });
  }

  public sendContactUsInformation(): void {
    let name: string = this.contactUsFormGroup.controls["nameFormControl"].value;
    let number: string = this.contactUsFormGroup.controls["phoneNumberFormControl"].value;
    let unformattedNumber = number.replace('(', '').replace(')', '').replace('-', '').replace(' ', '');
    let voiceConnectionDetails = new VoiceConnectionDetails();
    let personPortalState: PersonPortalState = LocalStorageUtilities.getPersonPortalState();
    voiceConnectionDetails.customerName = name;
    voiceConnectionDetails.customerDial = unformattedNumber;
    voiceConnectionDetails.dial_action = "monex_web_connect";
    voiceConnectionDetails.monexRep = personPortalState.mxna.acctRep;
    this.authorizationService.intiateCallToCustomer(voiceConnectionDetails).subscribe({
      next: () => { },
      error: (err: HttpErrorResponse) => { }
    });
    this.onHideContactUsPanelAction();
  }

  private promptForVersionUpdate() {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.available.subscribe(event => {
        // console.log('current version is', event.current);
        // console.log('available version is', event.available);
        this.isNewVersionAvailable = true;
      });
      this.checkForUpdateIntervalSubscribe = this.checkForUpdateInterval.subscribe(() => this.swUpdate.checkForUpdate()
        .then(() => console.log('checking for updates')));
    }
  }

  public downloadNewVersion(): void {
    this.swUpdate.activateUpdate().then(() => window.location.reload());
  }

  /**
   *  Form control error handlers.
   */
  public changePasswordFormHasError = (controlName: string, errorName: string) => {
    return this.changePasswordFormGroup.controls[controlName].hasError(errorName);
  }

  public changeUserIdFormHasError = (controlName: string, errorName: string) => {
    return this.changeUserIdFormGroup.controls[controlName].hasError(errorName);
  }

  public contactUsFormHasError = (controlName: string, errorName: string) => {
    return this.contactUsFormGroup.controls[controlName].hasError(errorName);
  }

  public userIdNotUniqueError = (controlName: string, errorName: string) => {
    return this.changeUserIdFormGroup.controls[controlName].hasError(errorName);
  }

  /**
   * 
   * Form level matching input validators.
   * 
   */
  private pwdMatchValidator(frm: FormGroup) {
    return frm.get('newPasswordFormControl').value === frm.get('confirmPasswordFormControl').value
      ? null : { 'mismatch': true };
  }

  private userIdMatchValidator(frm: FormGroup) {
    return frm.get('newUserIdFormControl').value === frm.get('confirmUserIdFormControl').value
      ? null : { 'mismatch': true };
  }

  // private phoneNumberMatchValidator(frm: FormGroup) {
  //   return frm.get('phoneNumberFormControl').value === frm.get('confirmUserIdFormControl').value
  //     ? null : { 'mismatch': true };
  // }

  private testValidator(control: AbstractControl) {
    // set error if the customer is not changing his User ID to a new value.
    let personPortalState: PersonPortalState = LocalStorageUtilities.getPersonPortalState();
    if (control.value === personPortalState.userName) {
      return { userIdNotUnique: true };
    }
    // The User ID value is unique.
    return null;
  }

  /**
   * Used to load the default account value in the "Select" control.
   */
  public objectComparisonFunction = function (option: any, value: any): boolean {
    return option.account === value.account;
  }

  /**
   * Used primarily in conjunction with setting keyboard focus on
   * elements that are not yet visible.  This is why the standard
   * Angular approach isn't sufficient.
   * @param elementName    * 
   */
  private setFocusOnElement(elementName: string) {
    let div: HTMLElement = this.document.querySelector(elementName);
    div.focus();
  }

  /**
   * Validate phone number field in the Contact Us panel.
   * 
   * @param field 
   * 
   */
  public validatePhoneNo(field: any) {
    var phoneNumDigits = field.value.replace(/\D/g, '');

    this.isValidFlg = (phoneNumDigits.length == 0 || phoneNumDigits.length == 10);

    var formattedNumber = phoneNumDigits;
    if (phoneNumDigits.length >= 6)
      formattedNumber = '(' + phoneNumDigits.substring(0, 3) + ') ' + phoneNumDigits.substring(3, 6) + '-' + phoneNumDigits.substring(6);
    else if (phoneNumDigits.length >= 3)
      formattedNumber = '(' + phoneNumDigits.substring(0, 3) + ') ' + phoneNumDigits.substring(3);
    field.value = formattedNumber;
  }

}
